import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Edit, Trash2, Loader2, Upload, X, Check, Image, Download, Upload as UploadIcon, Tag, Percent, FolderTree, Save, ChevronDown, ChevronRight, GripVertical, Calculator } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

import { API_URL, BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('list'); // list, categories
  const [editingPrices, setEditingPrices] = useState({}); // Track inline price edits
  const [savingPrices, setSavingPrices] = useState({});
  const [editingPurchase, setEditingPurchase] = useState({}); // Track inline purchase HT edits
  const [savingPurchase, setSavingPurchase] = useState({});
  const [editingMargin, setEditingMargin] = useState({}); // Track inline margin edits
  const [savingMargin, setSavingMargin] = useState({});
  const [editingDiscount, setEditingDiscount] = useState({}); // Track inline discount edits
  const [savingDiscount, setSavingDiscount] = useState({});
  const [pendingChanges, setPendingChanges] = useState({}); // Track all pending changes by product_id
  const [savingAll, setSavingAll] = useState(false);

  // Categories management
  const [categories, setCategories] = useState([
    { value: 'basic', label: 'Alarme Baseline', subcategories: ['hubs', 'detectors', 'sirens', 'keypads', 'accessories', 'kits'] },
    { value: 'video_basic', label: 'Vidéo Baseline', subcategories: ['cameras', 'nvr', 'accessories'] },
    { value: 'fire', label: 'Incendie Baseline', subcategories: ['detectors', 'sirens', 'accessories'] },
    { value: 'automation', label: 'Domotique', subcategories: ['relays', 'switches', 'sockets', 'accessories'] },
    { value: 'pro', label: 'Alarme Pro (Grade 2)', subcategories: ['hubs', 'detectors', 'sirens', 'accessories'] },
    { value: 'grade3', label: 'Alarme Pro (Grade 3)', subcategories: ['hubs', 'detectors', 'sirens', 'accessories'] },
    { value: 'video_pro', label: 'Vidéo Pro', subcategories: ['cameras', 'nvr', 'accessories'] },
    { value: 'ssi', label: 'SSI EN54', subcategories: ['hubs', 'sirens', 'callpoints', 'accessories'] },
  ]);

  const subcategoryLabels = {
    hubs: 'Centrales',
    detectors: 'Détecteurs',
    cameras: 'Caméras',
    sirens: 'Sirènes',
    keypads: 'Claviers',
    accessories: 'Accessoires',
    kits: 'Kits',
    nvr: 'NVR',
    relays: 'Relais',
    switches: 'Interrupteurs',
    sockets: 'Prises',
    callpoints: 'Déclencheurs',
  };

  const badgeOptions = [
    { value: 'new', label: 'Nouveau', color: 'bg-blue-500' },
    { value: 'promo', label: 'Promo', color: 'bg-red-500' },
    { value: 'bestseller', label: 'Best-seller', color: 'bg-orange-500' },
    { value: 'limited', label: 'Édition limitée', color: 'bg-purple-500' },
    { value: 'exclusive', label: 'Exclusif', color: 'bg-fgs-primary' },
  ];

  useEffect(() => {
    fetchProducts();
  }, [categoryFilter]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const url = categoryFilter 
        ? `${API}/admin/products?category=${categoryFilter}`
        : `${API}/admin/products`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update a single product locally without refreshing the entire list
  const updateProductLocally = (productId, updates) => {
    setProducts(prev => prev.map(p => 
      p.product_id === productId ? { ...p, ...updates } : p
    ));
  };

  // Add pending change for a product
  const addPendingChange = (productId, field, value) => {
    setPendingChanges(prev => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [field]: value
      }
    }));
  };

  // Calculate updates for a product based on field changes
  const calculateProductUpdates = (product, changes) => {
    const purchaseHT = changes.purchase_price_ht !== undefined 
      ? parseFloat(changes.purchase_price_ht) || 0 
      : product.purchase_price_ht || 0;
    
    const marginValue = changes.margin_value !== undefined 
      ? parseFloat(changes.margin_value) || 100 
      : product.margin_value || 100;
    
    const discountValue = changes.discount_value !== undefined 
      ? parseFloat(changes.discount_value) || 0 
      : product.discount_value || 0;
    
    const priceInput = changes.price !== undefined 
      ? parseFloat(changes.price) || 0 
      : null;
    
    if (purchaseHT <= 0) {
      // No purchase price - just update what was changed
      return changes;
    }
    
    // Calculate margin in euros
    const marginEuros = purchaseHT * (marginValue / 100);
    
    // Price HT before discount (purchase + margin)
    const priceHTBeforeDiscount = purchaseHT + marginEuros;
    const originalPriceTTC = priceHTBeforeDiscount * 1.20;
    
    // Apply discount
    let discountEuros = 0;
    if (discountValue > 0) {
      discountEuros = priceHTBeforeDiscount * (discountValue / 100);
    }
    
    const priceHT = priceHTBeforeDiscount - discountEuros;
    let priceTTC = priceHT * 1.20;
    
    // If price was directly changed, use that and recalculate discount
    if (priceInput !== null && priceInput > 0) {
      priceTTC = priceInput;
      const newPriceHT = priceTTC / 1.20;
      const newDiscountEuros = Math.max(0, priceHTBeforeDiscount - newPriceHT);
      const newDiscountPercent = priceHTBeforeDiscount > 0 
        ? Math.round((newDiscountEuros / priceHTBeforeDiscount) * 100) 
        : 0;
      
      return {
        purchase_price_ht: purchaseHT,
        margin_value: marginValue,
        margin_type: 'percent',
        discount_value: newDiscountPercent,
        discount_type: 'percent',
        price: priceTTC,
        price_ht: newPriceHT,
        original_price: newDiscountPercent > 0 ? originalPriceTTC : null,
        discount_percent: newDiscountPercent > 0 ? newDiscountPercent : null
      };
    }
    
    // Calculate discount percent for badge
    const discountPercent = discountValue > 0 ? Math.round(discountValue) : null;
    
    return {
      purchase_price_ht: purchaseHT,
      margin_value: marginValue,
      margin_type: 'percent',
      discount_value: discountValue,
      discount_type: 'percent',
      price: priceTTC,
      price_ht: priceHT,
      original_price: discountPercent > 0 ? originalPriceTTC : null,
      discount_percent: discountPercent
    };
  };

  // Save all pending changes at once
  const saveAllChanges = async () => {
    const productIds = Object.keys(pendingChanges);
    if (productIds.length === 0) {
      console.log('Info:', 'Aucune modification en attente');
      return;
    }
    
    setSavingAll(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const productId of productIds) {
      const product = products.find(p => p.product_id === productId);
      if (!product) continue;
      
      const changes = pendingChanges[productId];
      const updates = calculateProductUpdates(product, changes);
      
      try {
        const response = await fetch(`${API}/admin/products/${productId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updates)
        });
        
        if (response.ok) {
          updateProductLocally(productId, updates);
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }
    
    // Clear all pending changes and editing states
    setPendingChanges({});
    setEditingPrices({});
    setEditingPurchase({});
    setEditingMargin({});
    setEditingDiscount({});
    
    setSavingAll(false);
    
    if (errorCount === 0) {
      toast.success(`${successCount} produit(s) mis à jour`);
    } else {
      toast.warning(`${successCount} réussi(s), ${errorCount} erreur(s)`);
    }
  };

  // Cancel all pending changes
  const cancelAllChanges = () => {
    setPendingChanges({});
    setEditingPrices({});
    setEditingPurchase({});
    setEditingMargin({});
    setEditingDiscount({});
    console.log('Info:', 'Modifications annulées');
  };

  const pendingCount = Object.keys(pendingChanges).length;

  const handleDelete = async (productId) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    
    try {
      const response = await fetch(`${API}/admin/products/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        console.log('Success:', 'Produit supprimé');
        fetchProducts();
      } else {
        console.log('Error:', 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    }
  };

  // Quick price update - recalcule automatiquement la remise et le prix barré
  const handleQuickPriceUpdate = async (productId, newPrice) => {
    setSavingPrices(prev => ({ ...prev, [productId]: true }));
    try {
      const priceTTC = parseFloat(newPrice) || 0;
      
      // Trouver le produit actuel pour récupérer ses données de pricing
      const product = products.find(p => p.product_id === productId);
      
      let updateData = { price: priceTTC || null };
      
      // Si le produit a un prix d'achat et une marge, recalculer le prix barré et la remise
      if (product && product.purchase_price_ht && priceTTC > 0) {
        const purchaseHT = product.purchase_price_ht;
        const marginValue = product.margin_value || 100;
        const marginType = product.margin_type || 'percent';
        
        // Calculer la marge en euros
        let marginEuros = 0;
        if (marginType === 'percent') {
          marginEuros = purchaseHT * (marginValue / 100);
        } else {
          marginEuros = marginValue;
        }
        
        // Prix HT avant remise (prix d'achat + marge)
        const priceHTBeforeDiscount = purchaseHT + marginEuros;
        const originalPriceTTC = priceHTBeforeDiscount * 1.20;
        
        // Prix HT final
        const priceHT = priceTTC / 1.20;
        
        // Remise déduite
        const discountEuros = Math.max(0, priceHTBeforeDiscount - priceHT);
        
        // Calcul du % de remise pour le badge
        const discountPercent = discountEuros > 0.01 && priceHTBeforeDiscount > 0
          ? Math.round((discountEuros / priceHTBeforeDiscount) * 100)
          : null;
        
        // Mettre à jour toutes les données de pricing
        updateData = {
          price: priceTTC,
          price_ht: priceHT,
          original_price: discountPercent > 0 ? originalPriceTTC : null,
          discount_percent: discountPercent,
          discount_value: discountPercent || null,
          discount_type: 'percent'
        };
      }
      
      const response = await fetch(`${API}/admin/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        console.log('Success:', 'Prix mis à jour');
        // Update locally instead of fetching all products
        updateProductLocally(productId, updateData);
        setEditingPrices(prev => {
          const newState = { ...prev };
          delete newState[productId];
          return newState;
        });
      } else {
        console.log('Error:', 'Erreur');
      }
    } catch (error) {
      console.log('Error:', 'Erreur');
    } finally {
      setSavingPrices(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Quick purchase HT update - recalcule automatiquement les prix
  const handleQuickPurchaseUpdate = async (productId, newPurchaseHT) => {
    setSavingPurchase(prev => ({ ...prev, [productId]: true }));
    try {
      const purchaseHT = parseFloat(newPurchaseHT) || 0;
      const product = products.find(p => p.product_id === productId);
      
      if (!product || purchaseHT <= 0) {
        console.log('Error:', 'Prix d\'achat invalide');
        setSavingPurchase(prev => ({ ...prev, [productId]: false }));
        return;
      }
      
      // Récupérer la marge actuelle ou défaut à 100%
      const marginValue = product.margin_value || 100;
      const marginType = product.margin_type || 'percent';
      
      // Calculer la marge en euros
      let marginEuros = 0;
      if (marginType === 'percent') {
        marginEuros = purchaseHT * (marginValue / 100);
      } else {
        marginEuros = marginValue;
      }
      
      // Prix HT avant remise (achat + marge)
      const priceHTBeforeDiscount = purchaseHT + marginEuros;
      const originalPriceTTC = priceHTBeforeDiscount * 1.20;
      
      // Appliquer la remise si elle existe
      const discountValue = product.discount_value || 0;
      const discountType = product.discount_type || 'percent';
      
      let discountEuros = 0;
      if (discountType === 'percent' && discountValue > 0) {
        discountEuros = priceHTBeforeDiscount * (discountValue / 100);
      } else if (discountValue > 0) {
        discountEuros = discountValue;
      }
      
      const priceHT = priceHTBeforeDiscount - discountEuros;
      const priceTTC = priceHT * 1.20;
      
      // Calcul du % de remise pour le badge
      const discountPercent = discountEuros > 0.01 && priceHTBeforeDiscount > 0
        ? Math.round((discountEuros / priceHTBeforeDiscount) * 100)
        : null;
      
      const updateData = {
        purchase_price_ht: purchaseHT,
        margin_value: marginValue,
        margin_type: marginType,
        price: priceTTC,
        price_ht: priceHT,
        original_price: discountPercent > 0 ? originalPriceTTC : null,
        discount_percent: discountPercent
      };
      
      const response = await fetch(`${API}/admin/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        console.log('Success:', 'Prix d\'achat mis à jour');
        updateProductLocally(productId, updateData);
        setEditingPurchase(prev => {
          const newState = { ...prev };
          delete newState[productId];
          return newState;
        });
      } else {
        console.log('Error:', 'Erreur');
      }
    } catch (error) {
      console.log('Error:', 'Erreur');
    } finally {
      setSavingPurchase(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Quick margin update - recalcule automatiquement les prix
  const handleQuickMarginUpdate = async (productId, newMarginValue, marginType = 'percent') => {
    setSavingMargin(prev => ({ ...prev, [productId]: true }));
    try {
      const marginVal = parseFloat(newMarginValue) || 0;
      const product = products.find(p => p.product_id === productId);
      
      if (!product) {
        console.log('Error:', 'Produit non trouvé');
        setSavingMargin(prev => ({ ...prev, [productId]: false }));
        return;
      }
      
      // Si pas de prix d'achat, on ne peut pas calculer
      if (!product.purchase_price_ht || product.purchase_price_ht <= 0) {
        console.log('Error:', 'Définissez d\'abord un prix d\'achat HT');
        setSavingMargin(prev => ({ ...prev, [productId]: false }));
        return;
      }
      
      const purchaseHT = product.purchase_price_ht;
      
      // Calculer la marge en euros
      let marginEuros = 0;
      if (marginType === 'percent') {
        marginEuros = purchaseHT * (marginVal / 100);
      } else {
        marginEuros = marginVal;
      }
      
      // Prix HT avant remise (achat + marge)
      const priceHTBeforeDiscount = purchaseHT + marginEuros;
      const originalPriceTTC = priceHTBeforeDiscount * 1.20;
      
      // Appliquer la remise si elle existe
      const discountValue = product.discount_value || 0;
      const discountType = product.discount_type || 'percent';
      
      let discountEuros = 0;
      if (discountType === 'percent' && discountValue > 0) {
        discountEuros = priceHTBeforeDiscount * (discountValue / 100);
      } else if (discountValue > 0) {
        discountEuros = discountValue;
      }
      
      const priceHT = priceHTBeforeDiscount - discountEuros;
      const priceTTC = priceHT * 1.20;
      
      // Calcul du % de remise pour le badge
      const discountPercent = discountEuros > 0.01 && priceHTBeforeDiscount > 0
        ? Math.round((discountEuros / priceHTBeforeDiscount) * 100)
        : null;
      
      const updateData = {
        margin_value: marginVal,
        margin_type: marginType,
        price: priceTTC,
        price_ht: priceHT,
        original_price: discountPercent > 0 ? originalPriceTTC : null,
        discount_percent: discountPercent
      };
      
      const response = await fetch(`${API}/admin/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        console.log('Success:', 'Marge mise à jour');
        updateProductLocally(productId, updateData);
        setEditingMargin(prev => {
          const newState = { ...prev };
          delete newState[productId];
          return newState;
        });
      } else {
        console.log('Error:', 'Erreur');
      }
    } catch (error) {
      console.log('Error:', 'Erreur');
    } finally {
      setSavingMargin(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Quick discount update - recalcule automatiquement les prix
  const handleQuickDiscountUpdate = async (productId, newDiscountValue, discountType = 'percent') => {
    setSavingDiscount(prev => ({ ...prev, [productId]: true }));
    try {
      const discountVal = parseFloat(newDiscountValue) || 0;
      const product = products.find(p => p.product_id === productId);
      
      if (!product) {
        console.log('Error:', 'Produit non trouvé');
        setSavingDiscount(prev => ({ ...prev, [productId]: false }));
        return;
      }
      
      const purchaseHT = product.purchase_price_ht || 0;
      const marginValue = product.margin_value || 100;
      const marginType = product.margin_type || 'percent';
      
      let priceHTBeforeDiscount;
      let originalPriceTTC;
      
      if (purchaseHT > 0) {
        // Calculer depuis le prix d'achat + marge
        let marginEuros = 0;
        if (marginType === 'percent') {
          marginEuros = purchaseHT * (marginValue / 100);
        } else {
          marginEuros = marginValue;
        }
        priceHTBeforeDiscount = purchaseHT + marginEuros;
        originalPriceTTC = priceHTBeforeDiscount * 1.20;
      } else {
        // Utiliser le prix barré existant ou le prix actuel
        originalPriceTTC = product.original_price || product.price || 0;
        priceHTBeforeDiscount = originalPriceTTC / 1.20;
      }
      
      // Calculer la remise en euros
      let discountEuros = 0;
      if (discountType === 'percent' && discountVal > 0) {
        discountEuros = priceHTBeforeDiscount * (discountVal / 100);
      } else if (discountVal > 0) {
        discountEuros = discountVal;
      }
      
      const priceHT = priceHTBeforeDiscount - discountEuros;
      const priceTTC = priceHT * 1.20;
      
      // Calcul du % de remise pour le badge
      const discountPercent = discountVal > 0 && priceHTBeforeDiscount > 0
        ? Math.round((discountEuros / priceHTBeforeDiscount) * 100)
        : null;
      
      const updateData = {
        discount_value: discountVal,
        discount_type: discountType,
        price: priceTTC,
        price_ht: priceHT,
        original_price: discountPercent > 0 ? originalPriceTTC : null,
        discount_percent: discountPercent
      };
      
      const response = await fetch(`${API}/admin/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        console.log('Success:', 'Remise mise à jour');
        updateProductLocally(productId, updateData);
        setEditingDiscount(prev => {
          const newState = { ...prev };
          delete newState[productId];
          return newState;
        });
      } else {
        console.log('Error:', 'Erreur');
      }
    } catch (error) {
      console.log('Error:', 'Erreur');
    } finally {
      setSavingDiscount(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Export products to CSV
  const handleExport = () => {
    const headers = ['product_id', 'name', 'category', 'subcategory', 'price', 'original_price', 'badge', 'discount_percent', 'colors', 'image_url'];
    const csvContent = [
      headers.join(';'),
      ...products.map(p => [
        p.product_id,
        `"${p.name}"`,
        p.category || '',
        p.subcategory || '',
        p.price || '',
        p.original_price || '',
        p.badge || '',
        p.discount_percent || '',
        (p.colors || []).join(','),
        p.image_url || ''
      ].join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `products_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    console.log('Success:', 'Export terminé');
  };

  // Import products from CSV
  const handleSyncStorage = async () => {
    if (!window.confirm('Ajouter / mettre à jour les cartes SD Hikvision (128 Go, 256 Go) en base ?\n\nCette action est idempotente : sans effet si déjà présentes.')) return;
    try {
      const response = await fetch(`${API}/admin/products/sync-storage`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) {
        alert('Erreur : ' + (data.detail || 'Échec de la synchronisation'));
        return;
      }
      alert(`✅ ${data.message}\n\nAjoutés : ${data.inserted.join(', ') || 'aucun'}\nMis à jour : ${data.updated.join(', ') || 'aucun'}\nTotal cartes SD en base : ${data.total_storage_in_db}`);
      fetchProducts();
    } catch (error) {
      alert('Erreur réseau : ' + error.message);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split('\n');
      const headers = lines[0].split(';').map(h => h.trim());
      
      let updated = 0;
      let errors = 0;

      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        const values = lines[i].split(';');
        const productId = values[0];
        const price = values[4] ? parseFloat(values[4]) : null;
        const originalPrice = values[5] ? parseFloat(values[5]) : null;
        const badge = values[6] || null;
        const discountPercent = values[7] ? parseInt(values[7]) : null;

        if (productId && productId.startsWith('prod_')) {
          try {
            const response = await fetch(`${API}/admin/products/${productId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ 
                price, 
                original_price: originalPrice,
                badge,
                discount_percent: discountPercent
              })
            });
            if (response.ok) updated++;
            else errors++;
          } catch {
            errors++;
          }
        }
      }

      console.log('Success:', `Import terminé: ${updated} produits mis à jour, ${errors} erreurs`);
      fetchProducts();
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.product_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value || 'Non classé';
  };

  const getBadgeStyle = (badge) => {
    const opt = badgeOptions.find(b => b.value === badge);
    return opt ? opt.color : 'bg-gray-500';
  };

  const getBadgeLabel = (badge) => {
    const opt = badgeOptions.find(b => b.value === badge);
    return opt ? opt.label : badge;
  };

  return (
    <div>
      {/* Header with tabs */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="font-heading text-2xl font-bold text-white">Produits</h1>
          <div className="flex bg-fgs-bg rounded-lg p-1">
            <button
              onClick={() => setActiveTab('list')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                activeTab === 'list' ? 'bg-fgs-primary text-white' : 'text-fgs-text-muted hover:text-white'
              }`}
            >
              Liste
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                activeTab === 'categories' ? 'bg-fgs-primary text-white' : 'text-fgs-text-muted hover:text-white'
              }`}
            >
              Catégories
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            id="import-csv"
            accept=".csv"
            onChange={handleImport}
            className="hidden"
          />
          <Button 
            variant="outline"
            onClick={() => document.getElementById('import-csv').click()}
            className="border-fgs-border text-white"
          >
            <UploadIcon className="w-4 h-4 mr-2" />
            Importer
          </Button>
          <Button 
            variant="outline"
            onClick={handleExport}
            className="border-fgs-border text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Exporter
          </Button>
          <Button 
            onClick={() => setIsCreating(true)}
            className="bg-fgs-primary hover:bg-fgs-primary-hover"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau
          </Button>
        </div>
      </div>

      {activeTab === 'list' && (
        <>
          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fgs-text-muted" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un produit..."
                className="pl-10 bg-fgs-surface border-fgs-border text-white"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 bg-fgs-surface border border-fgs-border rounded-lg text-white"
            >
              <option value="">Toutes les catégories</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Bulk Actions Bar */}
          <div className="bg-fgs-bg border border-fgs-border rounded-lg p-4 mb-4">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-white font-medium text-sm">
                <Percent className="w-4 h-4 inline mr-1" />
                Modification en masse
              </span>
              
              <select
                id="bulk-category"
                className="px-3 py-2 bg-fgs-surface border border-fgs-border rounded-lg text-white text-sm"
                defaultValue=""
              >
                <option value="">Catégorie cible...</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              
              <div className="flex items-center gap-2">
                <select
                  id="bulk-action"
                  className="px-3 py-2 bg-fgs-surface border border-fgs-border rounded-lg text-white text-sm"
                  defaultValue="increase"
                >
                  <option value="increase">Augmenter de</option>
                  <option value="decrease">Diminuer de</option>
                  <option value="set_margin">Définir marge à</option>
                  <option value="set_discount">Définir remise à</option>
                </select>
                
                <Input
                  id="bulk-value"
                  type="number"
                  step="0.01"
                  placeholder="0"
                  className="w-20 bg-fgs-surface border-fgs-border text-white text-sm"
                />
                
                <select
                  id="bulk-type"
                  className="w-16 px-2 py-2 bg-fgs-surface border border-fgs-border rounded-lg text-white text-sm"
                  defaultValue="percent"
                >
                  <option value="percent">%</option>
                  <option value="euros">€</option>
                </select>
              </div>
              
              <Button
                onClick={async () => {
                  const category = document.getElementById('bulk-category').value;
                  const action = document.getElementById('bulk-action').value;
                  const value = parseFloat(document.getElementById('bulk-value').value) || 0;
                  const type = document.getElementById('bulk-type').value;
                  
                  if (!category) {
                    console.log('Error:', 'Sélectionnez une catégorie');
                    return;
                  }
                  if (value === 0) {
                    console.log('Error:', 'Entrez une valeur');
                    return;
                  }
                  
                  const targetProducts = products.filter(p => p.category === category);
                  if (targetProducts.length === 0) {
                    console.log('Error:', 'Aucun produit dans cette catégorie');
                    return;
                  }
                  
                  if (!window.confirm(`Appliquer cette modification à ${targetProducts.length} produits ?`)) return;
                  
                  let updated = 0;
                  for (const product of targetProducts) {
                    try {
                      const purchaseHT = product.purchase_price_ht || 0;
                      let marginValue = product.margin_value || 100;
                      const marginType = product.margin_type || 'percent';
                      let discountValue = product.discount_value || 0;
                      const discountType = product.discount_type || 'percent';
                      
                      // Appliquer l'action demandée
                      if (action === 'increase') {
                        // Augmenter le prix d'achat HT
                        if (type === 'percent') {
                          // Augmenter prix final = augmenter le prix d'achat
                          // On recalcule le prix d'achat pour obtenir le nouveau prix
                          const currentPriceHT = product.price ? product.price / 1.20 : 0;
                          const newPriceHT = currentPriceHT * (1 + value / 100);
                          // Recalculer le prix d'achat en gardant la marge
                          if (purchaseHT > 0) {
                            const newPurchaseHT = purchaseHT * (1 + value / 100);
                            const response = await fetch(`${API}/admin/products/${product.product_id}`, {
                              method: 'PUT',
                              credentials: 'include',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                purchase_price_ht: newPurchaseHT
                              })
                            });
                            if (response.ok) {
                              // Trigger recalcul via handleQuickPurchaseUpdate logic
                              await handleQuickPurchaseUpdate(product.product_id, newPurchaseHT);
                              updated++;
                              continue;
                            }
                          }
                        } else {
                          // Augmenter en euros - on ajoute au prix d'achat
                          if (purchaseHT > 0) {
                            const newPurchaseHT = purchaseHT + value;
                            await handleQuickPurchaseUpdate(product.product_id, newPurchaseHT);
                            updated++;
                            continue;
                          }
                        }
                      } else if (action === 'decrease') {
                        // Diminuer le prix d'achat HT
                        if (type === 'percent') {
                          if (purchaseHT > 0) {
                            const newPurchaseHT = purchaseHT * (1 - value / 100);
                            await handleQuickPurchaseUpdate(product.product_id, newPurchaseHT);
                            updated++;
                            continue;
                          }
                        } else {
                          if (purchaseHT > 0) {
                            const newPurchaseHT = Math.max(0, purchaseHT - value);
                            await handleQuickPurchaseUpdate(product.product_id, newPurchaseHT);
                            updated++;
                            continue;
                          }
                        }
                      } else if (action === 'set_margin') {
                        // Définir la marge
                        if (purchaseHT > 0) {
                          await handleQuickMarginUpdate(product.product_id, value, type);
                          updated++;
                          continue;
                        } else {
                          // Pas de prix d'achat, on skip
                          continue;
                        }
                      } else if (action === 'set_discount') {
                        // Définir la remise - recalcule tout depuis le prix d'achat + marge
                        if (purchaseHT > 0) {
                          let marginEuros = 0;
                          if (marginType === 'percent') {
                            marginEuros = purchaseHT * (marginValue / 100);
                          } else {
                            marginEuros = marginValue;
                          }
                          
                          const priceHTBeforeDiscount = purchaseHT + marginEuros;
                          const originalPriceTTC = priceHTBeforeDiscount * 1.20;
                          
                          // Appliquer la nouvelle remise
                          let newDiscountEuros = 0;
                          if (type === 'percent') {
                            newDiscountEuros = priceHTBeforeDiscount * (value / 100);
                          } else {
                            newDiscountEuros = value;
                          }
                          
                          const priceHT = priceHTBeforeDiscount - newDiscountEuros;
                          const priceTTC = priceHT * 1.20;
                          const discountPercent = value > 0 ? Math.round((newDiscountEuros / priceHTBeforeDiscount) * 100) : null;
                          
                          const response = await fetch(`${API}/admin/products/${product.product_id}`, {
                            method: 'PUT',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              discount_value: value,
                              discount_type: type,
                              price: priceTTC,
                              price_ht: priceHT,
                              original_price: discountPercent > 0 ? originalPriceTTC : null,
                              discount_percent: discountPercent
                            })
                          });
                          if (response.ok) updated++;
                          continue;
                        }
                      }
                      
                      // Fallback pour les produits sans prix d'achat: modification simple du prix
                      let newPrice = product.price || 0;
                      let newOriginalPrice = product.original_price;
                      let newDiscountPercent = product.discount_percent;
                      
                      if (action === 'increase') {
                        if (type === 'percent') {
                          newPrice = newPrice * (1 + value / 100);
                          if (newOriginalPrice) newOriginalPrice = newOriginalPrice * (1 + value / 100);
                        } else {
                          newPrice = newPrice + value;
                          if (newOriginalPrice) newOriginalPrice = newOriginalPrice + value;
                        }
                      } else if (action === 'decrease') {
                        if (type === 'percent') {
                          newPrice = newPrice * (1 - value / 100);
                          if (newOriginalPrice) newOriginalPrice = newOriginalPrice * (1 - value / 100);
                        } else {
                          newPrice = newPrice - value;
                          if (newOriginalPrice) newOriginalPrice = newOriginalPrice - value;
                        }
                      }
                      
                      const response = await fetch(`${API}/admin/products/${product.product_id}`, {
                        method: 'PUT',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          price: Math.round(newPrice * 100) / 100,
                          original_price: newOriginalPrice ? Math.round(newOriginalPrice * 100) / 100 : null,
                          discount_percent: newDiscountPercent
                        })
                      });
                      if (response.ok) updated++;
                    } catch (e) {
                      console.error(e);
                    }
                  }
                  
                  console.log('Success:', `${updated} produits mis à jour`);
                  fetchProducts();
                }}
                className="bg-fgs-primary hover:bg-fgs-primary-hover text-sm"
              >
                Appliquer
              </Button>
              
              <span className="text-fgs-text-muted text-xs ml-auto">
                {categoryFilter ? `${filteredProducts.length} produits affichés` : `${products.length} produits au total`}
              </span>
            </div>
          </div>

          {/* Products Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-fgs-primary" />
            </div>
          ) : (
            <>
              {/* Floating save bar when there are pending changes */}
              {pendingCount > 0 && (
                <div className="sticky top-0 z-20 bg-fgs-primary/95 backdrop-blur-sm border-b border-fgs-primary rounded-t-xl px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <span className="text-white font-bold">{pendingCount}</span>
                    </div>
                    <span className="text-white font-medium">
                      {pendingCount} modification{pendingCount > 1 ? 's' : ''} en attente
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={cancelAllChanges}
                      variant="ghost"
                      className="text-white hover:bg-white/20"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Annuler tout
                    </Button>
                    <Button
                      onClick={saveAllChanges}
                      disabled={savingAll}
                      className="bg-white text-fgs-primary hover:bg-white/90 font-bold"
                    >
                      {savingAll ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Valider tout
                    </Button>
                  </div>
                </div>
              )}
              <div className="bg-fgs-surface border border-fgs-border rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-fgs-bg">
                  <tr>
                    <th className="text-left px-3 py-3 text-xs font-medium text-fgs-text-muted">Image</th>
                    <th className="text-left px-3 py-3 text-xs font-medium text-fgs-text-muted">Produit</th>
                    <th className="text-left px-3 py-3 text-xs font-medium text-fgs-text-muted">Catégorie</th>
                    <th className="text-right px-3 py-3 text-xs font-medium text-fgs-text-muted">Achat HT</th>
                    <th className="text-right px-3 py-3 text-xs font-medium text-fgs-text-muted">Marge</th>
                    <th className="text-right px-3 py-3 text-xs font-medium text-fgs-text-muted">Remise</th>
                    <th className="text-right px-3 py-3 text-xs font-medium text-fgs-text-muted">Prix TTC</th>
                    <th className="text-right px-3 py-3 text-xs font-medium text-fgs-text-muted">Prix barré</th>
                    <th className="text-center px-3 py-3 text-xs font-medium text-fgs-text-muted">Badge</th>
                    <th className="text-right px-3 py-3 text-xs font-medium text-fgs-text-muted">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(product => {
                    // Calculate margin info
                    const priceHT = product.price ? product.price / 1.20 : 0;
                    const purchaseHT = product.purchase_price_ht || 0;
                    const marginEuros = purchaseHT > 0 ? priceHT - purchaseHT : 0;
                    const marginPercent = purchaseHT > 0 ? (marginEuros / purchaseHT) * 100 : 0;
                    
                    return (
                    <tr key={product.product_id} className="border-t border-fgs-border hover:bg-fgs-bg/50">
                      <td className="px-3 py-2">
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-10 h-10 object-contain rounded bg-white"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/40'}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <p className="text-white font-medium text-sm truncate max-w-[200px]">{product.name}</p>
                        <p className="text-[10px] text-fgs-text-muted font-mono">{product.product_id}</p>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="px-1.5 py-0.5 bg-fgs-bg rounded text-[10px] text-white inline-block w-fit">
                            {getCategoryLabel(product.category)}
                          </span>
                          {product.subcategory && (
                            <span className="text-[10px] text-fgs-text-muted">
                              {subcategoryLabels[product.subcategory] || product.subcategory}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {editingPurchase[product.product_id] !== undefined ? (
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              type="number"
                              step="0.01"
                              value={editingPurchase[product.product_id]}
                              onChange={(e) => setEditingPurchase(prev => ({
                                ...prev,
                                [product.product_id]: e.target.value
                              }))}
                              onBlur={() => {
                                const value = editingPurchase[product.product_id];
                                if (value !== '' && value !== undefined) {
                                  addPendingChange(product.product_id, 'purchase_price_ht', value);
                                }
                                setEditingPurchase(prev => {
                                  const newState = { ...prev };
                                  delete newState[product.product_id];
                                  return newState;
                                });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                } else if (e.key === 'Escape') {
                                  setEditingPurchase(prev => {
                                    const newState = { ...prev };
                                    delete newState[product.product_id];
                                    return newState;
                                  });
                                }
                              }}
                              autoFocus
                              className="w-20 h-7 bg-fgs-bg border-fgs-border text-white text-sm"
                              placeholder="0.00"
                            />
                          </div>
                        ) : (
                          <div 
                            className={`cursor-pointer group inline-block ${pendingChanges[product.product_id]?.purchase_price_ht !== undefined ? 'ring-2 ring-fgs-primary ring-offset-2 ring-offset-fgs-surface rounded px-1' : ''}`}
                            onClick={() => setEditingPurchase(prev => ({
                              ...prev,
                              [product.product_id]: pendingChanges[product.product_id]?.purchase_price_ht || purchaseHT || ''
                            }))}
                          >
                            {purchaseHT > 0 ? (
                              <span className="font-mono text-sm text-fgs-text-muted group-hover:text-fgs-primary">{purchaseHT.toFixed(2)} €</span>
                            ) : (
                              <span className="text-fgs-text-muted text-xs group-hover:text-fgs-primary">+ Ajouter</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {editingMargin[product.product_id] !== undefined ? (
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              type="number"
                              step="1"
                              value={editingMargin[product.product_id]}
                              onChange={(e) => setEditingMargin(prev => ({
                                ...prev,
                                [product.product_id]: e.target.value
                              }))}
                              onBlur={() => {
                                const value = editingMargin[product.product_id];
                                if (value !== '' && value !== undefined) {
                                  addPendingChange(product.product_id, 'margin_value', value);
                                }
                                setEditingMargin(prev => {
                                  const newState = { ...prev };
                                  delete newState[product.product_id];
                                  return newState;
                                });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                } else if (e.key === 'Escape') {
                                  setEditingMargin(prev => {
                                    const newState = { ...prev };
                                    delete newState[product.product_id];
                                    return newState;
                                  });
                                }
                              }}
                              autoFocus
                              className="w-16 h-7 bg-fgs-bg border-fgs-border text-white text-sm"
                              placeholder="100"
                            />
                            <span className="text-fgs-text-muted text-xs">%</span>
                          </div>
                        ) : (
                          <div 
                            className={`cursor-pointer group inline-block ${pendingChanges[product.product_id]?.margin_value !== undefined ? 'ring-2 ring-fgs-primary ring-offset-2 ring-offset-fgs-surface rounded px-1' : ''}`}
                            onClick={() => setEditingMargin(prev => ({
                              ...prev,
                              [product.product_id]: pendingChanges[product.product_id]?.margin_value || product.margin_value || marginPercent.toFixed(0) || '100'
                            }))}
                          >
                            {purchaseHT > 0 ? (
                              <div>
                                <span className={`font-mono text-sm group-hover:text-fgs-primary ${marginPercent >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  {marginPercent.toFixed(0)}%
                                </span>
                                <p className="text-[10px] text-fgs-text-muted">{marginEuros.toFixed(2)} €</p>
                              </div>
                            ) : (
                              <span className="text-fgs-text-muted text-xs group-hover:text-fgs-primary">—</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {editingDiscount[product.product_id] !== undefined ? (
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              type="number"
                              step="1"
                              value={editingDiscount[product.product_id]}
                              onChange={(e) => setEditingDiscount(prev => ({
                                ...prev,
                                [product.product_id]: e.target.value
                              }))}
                              onBlur={() => {
                                const value = editingDiscount[product.product_id];
                                if (value !== '' && value !== undefined) {
                                  addPendingChange(product.product_id, 'discount_value', value);
                                }
                                setEditingDiscount(prev => {
                                  const newState = { ...prev };
                                  delete newState[product.product_id];
                                  return newState;
                                });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                } else if (e.key === 'Escape') {
                                  setEditingDiscount(prev => {
                                    const newState = { ...prev };
                                    delete newState[product.product_id];
                                    return newState;
                                  });
                                }
                              }}
                              autoFocus
                              className="w-14 h-7 bg-fgs-bg border-fgs-border text-white text-sm"
                              placeholder="0"
                            />
                            <span className="text-fgs-text-muted text-xs">%</span>
                          </div>
                        ) : (
                          <div 
                            className={`cursor-pointer group inline-block ${pendingChanges[product.product_id]?.discount_value !== undefined ? 'ring-2 ring-fgs-primary ring-offset-2 ring-offset-fgs-surface rounded px-1' : ''}`}
                            onClick={() => setEditingDiscount(prev => ({
                              ...prev,
                              [product.product_id]: pendingChanges[product.product_id]?.discount_value || product.discount_percent || ''
                            }))}
                          >
                            {product.discount_percent ? (
                              <span className="font-mono text-sm text-red-400 group-hover:text-red-300">-{product.discount_percent}%</span>
                            ) : (
                              <span className="text-fgs-text-muted text-xs group-hover:text-fgs-primary">+ Remise</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {editingPrices[product.product_id] !== undefined ? (
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              type="number"
                              step="0.01"
                              value={editingPrices[product.product_id]}
                              onChange={(e) => setEditingPrices(prev => ({
                                ...prev,
                                [product.product_id]: e.target.value
                              }))}
                              onBlur={() => {
                                // Auto-save to pending changes when leaving the field
                                const value = editingPrices[product.product_id];
                                if (value !== '' && value !== undefined) {
                                  addPendingChange(product.product_id, 'price', value);
                                }
                                setEditingPrices(prev => {
                                  const newState = { ...prev };
                                  delete newState[product.product_id];
                                  return newState;
                                });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                } else if (e.key === 'Escape') {
                                  setEditingPrices(prev => {
                                    const newState = { ...prev };
                                    delete newState[product.product_id];
                                    return newState;
                                  });
                                }
                              }}
                              autoFocus
                              className="w-20 h-7 bg-fgs-bg border-fgs-border text-white text-sm"
                            />
                          </div>
                        ) : (
                          <div 
                            className={`cursor-pointer group inline-block ${pendingChanges[product.product_id]?.price !== undefined ? 'ring-2 ring-fgs-primary ring-offset-2 ring-offset-fgs-surface rounded px-1' : ''}`}
                            onClick={() => setEditingPrices(prev => ({
                              ...prev,
                              [product.product_id]: pendingChanges[product.product_id]?.price || product.price || ''
                            }))}
                          >
                            {product.price ? (
                              <span className="text-fgs-primary font-mono font-bold text-sm">{product.price.toFixed(2)} €</span>
                            ) : (
                              <span className="text-fgs-text-muted text-xs">Devis</span>
                            )}
                            <Edit className="w-3 h-3 text-fgs-text-muted opacity-0 group-hover:opacity-100 inline ml-1" />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {product.original_price ? (
                          <span className="font-mono text-sm text-fgs-text-muted line-through">{product.original_price.toFixed(2)} €</span>
                        ) : (
                          <span className="text-fgs-text-muted text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {product.badge ? (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] text-white ${getBadgeStyle(product.badge)}`}>
                            {getBadgeLabel(product.badge)}
                          </span>
                        ) : product.discount_percent ? (
                          <span className="px-1.5 py-0.5 rounded text-[10px] text-white bg-red-500">
                            -{product.discount_percent}%
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setEditingProduct(product)}
                            className="p-1.5 hover:bg-fgs-bg rounded text-fgs-text-muted hover:text-white transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(product.product_id)}
                            className="p-1.5 hover:bg-fgs-accent/20 rounded text-fgs-text-muted hover:text-fgs-accent transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {filteredProducts.length === 0 && (
                <div className="text-center py-12 text-fgs-text-muted">
                  Aucun produit trouvé
                </div>
              )}
            </div>
            </>
          )}
        </>
      )}

      {activeTab === 'categories' && (
        <CategoriesManager 
          categories={categories} 
          setCategories={setCategories}
          subcategoryLabels={subcategoryLabels}
          products={products}
          onProductUpdate={fetchProducts}
        />
      )}

      {/* Product Editor Modal */}
      {(editingProduct || isCreating) && (
        <ProductEditor
          product={editingProduct}
          isCreating={isCreating}
          onClose={() => {
            setEditingProduct(null);
            setIsCreating(false);
          }}
          onSave={() => {
            fetchProducts();
            setEditingProduct(null);
            setIsCreating(false);
          }}
          categories={categories}
          subcategoryLabels={subcategoryLabels}
          badgeOptions={badgeOptions}
        />
      )}
    </div>
  );
};

// Categories Manager Component
const CategoriesManager = ({ categories, subcategoryLabels, products, onProductUpdate }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [draggedProduct, setDraggedProduct] = useState(null);

  const getProductsInCategory = (categoryValue, subcategoryValue = null) => {
    return products.filter(p => {
      if (subcategoryValue) {
        return p.category === categoryValue && p.subcategory === subcategoryValue;
      }
      return p.category === categoryValue;
    });
  };

  const handleDrop = async (categoryValue, subcategoryValue = null) => {
    if (!draggedProduct) return;

    try {
      const response = await fetch(`${API}/admin/products/${draggedProduct.product_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          category: categoryValue,
          subcategory: subcategoryValue
        })
      });

      if (response.ok) {
        console.log('Success:', 'Produit déplacé');
        onProductUpdate();
      }
    } catch (error) {
      console.log('Error:', 'Erreur');
    }
    setDraggedProduct(null);
  };

  return (
    <div className="space-y-4">
      <p className="text-fgs-text-muted text-sm mb-4">
        Glissez-déposez les produits pour les déplacer entre catégories.
      </p>

      {categories.map(cat => (
        <div 
          key={cat.value}
          className="bg-fgs-surface border border-fgs-border rounded-xl overflow-hidden"
        >
          <div 
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-fgs-bg/50"
            onClick={() => setExpandedCategory(expandedCategory === cat.value ? null : cat.value)}
          >
            <div className="flex items-center gap-3">
              <FolderTree className="w-5 h-5 text-fgs-primary" />
              <span className="font-medium text-white">{cat.label}</span>
              <span className="text-xs text-fgs-text-muted bg-fgs-bg px-2 py-0.5 rounded">
                {getProductsInCategory(cat.value).length} produits
              </span>
            </div>
            {expandedCategory === cat.value ? (
              <ChevronDown className="w-5 h-5 text-fgs-text-muted" />
            ) : (
              <ChevronRight className="w-5 h-5 text-fgs-text-muted" />
            )}
          </div>

          {expandedCategory === cat.value && (
            <div className="border-t border-fgs-border">
              {cat.subcategories.map(sub => {
                const subProducts = getProductsInCategory(cat.value, sub);
                return (
                  <div 
                    key={sub}
                    className="p-3 border-b border-fgs-border/50 last:border-b-0"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(cat.value, sub)}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-fgs-text-muted">{subcategoryLabels[sub] || sub}</span>
                      <span className="text-xs text-fgs-text-muted">({subProducts.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {subProducts.map(p => (
                        <div
                          key={p.product_id}
                          draggable
                          onDragStart={() => setDraggedProduct(p)}
                          className="flex items-center gap-2 px-2 py-1 bg-fgs-bg rounded cursor-move hover:bg-fgs-surface-highlight"
                        >
                          <GripVertical className="w-3 h-3 text-fgs-text-muted" />
                          <img src={p.image_url} alt="" className="w-6 h-6 object-contain rounded bg-white" />
                          <span className="text-xs text-white truncate max-w-32">{p.name}</span>
                        </div>
                      ))}
                      {subProducts.length === 0 && (
                        <span className="text-xs text-fgs-text-muted italic">Déposez des produits ici</span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Products without subcategory */}
              <div 
                className="p-3 bg-fgs-bg/30"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(cat.value, null)}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-fgs-text-muted">Sans sous-catégorie</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {getProductsInCategory(cat.value).filter(p => !p.subcategory).map(p => (
                    <div
                      key={p.product_id}
                      draggable
                      onDragStart={() => setDraggedProduct(p)}
                      className="flex items-center gap-2 px-2 py-1 bg-fgs-bg rounded cursor-move hover:bg-fgs-surface-highlight"
                    >
                      <GripVertical className="w-3 h-3 text-fgs-text-muted" />
                      <img src={p.image_url} alt="" className="w-6 h-6 object-contain rounded bg-white" />
                      <span className="text-xs text-white truncate max-w-32">{p.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Uncategorized products */}
      <div 
        className="bg-fgs-surface border border-dashed border-fgs-border rounded-xl p-4"
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => handleDrop(null, null)}
      >
        <div className="flex items-center gap-3 mb-3">
          <span className="font-medium text-fgs-text-muted">Non classés</span>
          <span className="text-xs text-fgs-text-muted bg-fgs-bg px-2 py-0.5 rounded">
            {products.filter(p => !p.category).length} produits
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {products.filter(p => !p.category).map(p => (
            <div
              key={p.product_id}
              draggable
              onDragStart={() => setDraggedProduct(p)}
              className="flex items-center gap-2 px-2 py-1 bg-fgs-bg rounded cursor-move hover:bg-fgs-surface-highlight"
            >
              <GripVertical className="w-3 h-3 text-fgs-text-muted" />
              <img src={p.image_url} alt="" className="w-6 h-6 object-contain rounded bg-white" />
              <span className="text-xs text-white truncate max-w-32">{p.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Product Editor Modal
const ProductEditor = ({ product, isCreating, onClose, onSave, categories, subcategoryLabels, badgeOptions }) => {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    description_long: product?.description_long || '',
    category: product?.category || '',
    subcategory: product?.subcategory || '',
    price: product?.price || '',
    original_price: product?.original_price || '',
    discount_percent: product?.discount_percent || '',
    badge: product?.badge || '',
    image_url: product?.image_url || '',
    images: product?.images || {},
    gallery: product?.gallery || [],
    colors: product?.colors || ['black'],
    specs: product?.specs || {},
    features: product?.features || [],
    ai_enabled: product?.ai_enabled || false,
    coming_soon: product?.coming_soon || false,
    guarantee_years: product?.guarantee_years || 4,
    // Pricing fields
    purchase_price_ht: product?.purchase_price_ht || '',
    margin_value: product?.margin_value || '',
    margin_type: product?.margin_type || 'percent',
    discount_value: product?.discount_value || '',
    discount_type: product?.discount_type || 'percent',
    price_ht: product?.price_ht || ''
  });

  // Helper function to recalculate all prices
  const recalculatePrices = (updates) => {
    const newData = { ...formData, ...updates };
    
    const purchaseHT = parseFloat(newData.purchase_price_ht) || 0;
    const marginValue = parseFloat(newData.margin_value) || 0;
    const marginType = newData.margin_type;
    const discountValue = parseFloat(newData.discount_value) || 0;
    const discountType = newData.discount_type;
    
    // Calculate margin in euros
    let marginEuros = 0;
    if (marginType === 'percent') {
      marginEuros = purchaseHT * (marginValue / 100);
    } else {
      marginEuros = marginValue;
    }
    
    // Prix HT avant remise
    const priceHTBeforeDiscount = purchaseHT + marginEuros;
    
    // Calculate discount in euros
    let discountEuros = 0;
    if (discountType === 'percent') {
      discountEuros = priceHTBeforeDiscount * (discountValue / 100);
    } else {
      discountEuros = discountValue;
    }
    
    // Prix HT après remise
    const priceHT = priceHTBeforeDiscount - discountEuros;
    
    // Prix TTC
    const priceTTC = priceHT * 1.20;
    const originalPriceTTC = priceHTBeforeDiscount * 1.20;
    
    // Calcul du % de remise pour le badge
    const discountPercent = discountEuros > 0 && priceHTBeforeDiscount > 0
      ? Math.round((discountEuros / priceHTBeforeDiscount) * 100)
      : '';
    
    // Calcul de la marge résiduelle après remise
    const finalMarginEuros = priceHT - purchaseHT;
    const finalMarginPercent = purchaseHT > 0 ? (finalMarginEuros / purchaseHT) * 100 : 0;
    
    return {
      ...newData,
      price_ht: priceHT > 0 ? priceHT.toFixed(2) : '',
      price: priceTTC > 0 ? priceTTC.toFixed(2) : '',
      original_price: discountEuros > 0 ? originalPriceTTC.toFixed(2) : '',
      discount_percent: discountPercent,
      // Computed values for display
      _marginEuros: marginEuros,
      _discountEuros: discountEuros,
      _priceHTBeforeDiscount: priceHTBeforeDiscount,
      _finalMarginEuros: finalMarginEuros,
      _finalMarginPercent: finalMarginPercent
    };
  };

  // Recalculate backwards from final price TTC
  const recalculateFromPriceTTC = (newPriceTTC) => {
    const purchaseHT = parseFloat(formData.purchase_price_ht) || 0;
    const marginValue = parseFloat(formData.margin_value) || 0;
    const marginType = formData.margin_type;
    
    // Prix HT from TTC
    const priceHT = newPriceTTC / 1.20;
    
    // Si pas de prix d'achat défini, on peut quand même calculer HT/TTC
    if (purchaseHT === 0) {
      return {
        ...formData,
        price: newPriceTTC.toFixed(2),
        price_ht: priceHT.toFixed(2),
        _finalMarginEuros: 0,
        _finalMarginPercent: 0,
        _marginEuros: 0,
        _discountEuros: 0,
        _priceHTBeforeDiscount: priceHT
      };
    }
    
    // Calculate margin in euros
    let marginEuros = 0;
    if (marginType === 'percent') {
      marginEuros = purchaseHT * (marginValue / 100);
    } else {
      marginEuros = marginValue;
    }
    
    // Prix HT avant remise (prix d'achat + marge)
    const priceHTBeforeDiscount = purchaseHT + marginEuros;
    const originalPriceTTC = priceHTBeforeDiscount * 1.20;
    
    // Remise déduite = Prix avant remise - Prix final
    const discountEuros = Math.max(0, priceHTBeforeDiscount - priceHT);
    
    // Calcul du % de remise pour le badge
    const discountPercent = discountEuros > 0.01 && priceHTBeforeDiscount > 0
      ? Math.round((discountEuros / priceHTBeforeDiscount) * 100)
      : '';
    
    // Marge résiduelle après remise
    const finalMarginEuros = priceHT - purchaseHT;
    const finalMarginPercent = purchaseHT > 0 ? (finalMarginEuros / purchaseHT) * 100 : 0;
    
    // Determine discount value based on current type setting
    let newDiscountValue = '';
    if (discountEuros > 0.01) {
      if (formData.discount_type === 'percent' && priceHTBeforeDiscount > 0) {
        newDiscountValue = ((discountEuros / priceHTBeforeDiscount) * 100).toFixed(1);
      } else {
        newDiscountValue = discountEuros.toFixed(2);
      }
    }
    
    return {
      ...formData,
      price: newPriceTTC.toFixed(2),
      price_ht: priceHT.toFixed(2),
      original_price: discountEuros > 0.01 ? originalPriceTTC.toFixed(2) : '',
      discount_value: newDiscountValue,
      discount_percent: discountPercent,
      // Computed values for display
      _marginEuros: marginEuros,
      _discountEuros: discountEuros,
      _priceHTBeforeDiscount: priceHTBeforeDiscount,
      _finalMarginEuros: finalMarginEuros,
      _finalMarginPercent: finalMarginPercent
    };
  };

  // Recalculate backwards from price HT
  const recalculateFromPriceHT = (newPriceHT) => {
    const purchaseHT = parseFloat(formData.purchase_price_ht) || 0;
    const marginValue = parseFloat(formData.margin_value) || 0;
    const marginType = formData.margin_type;
    
    // Prix TTC from HT
    const priceTTC = newPriceHT * 1.20;
    
    // Calculate margin in euros
    let marginEuros = 0;
    if (marginType === 'percent') {
      marginEuros = purchaseHT * (marginValue / 100);
    } else {
      marginEuros = marginValue;
    }
    
    // Prix HT avant remise
    const priceHTBeforeDiscount = purchaseHT + marginEuros;
    const originalPriceTTC = priceHTBeforeDiscount * 1.20;
    
    // Remise déduite
    const discountEuros = priceHTBeforeDiscount - newPriceHT;
    
    // % de remise
    const discountPercent = discountEuros > 0 && priceHTBeforeDiscount > 0
      ? Math.round((discountEuros / priceHTBeforeDiscount) * 100)
      : '';
    
    // Marge résiduelle
    const finalMarginEuros = newPriceHT - purchaseHT;
    const finalMarginPercent = purchaseHT > 0 ? (finalMarginEuros / purchaseHT) * 100 : 0;
    
    // Update discount value
    let newDiscountValue = '';
    if (discountEuros > 0) {
      if (formData.discount_type === 'percent' && priceHTBeforeDiscount > 0) {
        newDiscountValue = ((discountEuros / priceHTBeforeDiscount) * 100).toFixed(1);
      } else {
        newDiscountValue = discountEuros.toFixed(2);
      }
    }
    
    return {
      ...formData,
      price_ht: newPriceHT.toFixed(2),
      price: priceTTC > 0 ? priceTTC.toFixed(2) : '',
      original_price: discountEuros > 0 ? originalPriceTTC.toFixed(2) : '',
      discount_value: newDiscountValue,
      discount_percent: discountPercent,
      _marginEuros: marginEuros,
      _discountEuros: discountEuros,
      _priceHTBeforeDiscount: priceHTBeforeDiscount,
      _finalMarginEuros: finalMarginEuros,
      _finalMarginPercent: finalMarginPercent
    };
  };

  const [newSpec, setNewSpec] = useState({ key: '', value: '' });
  const [newFeature, setNewFeature] = useState('');
  const [newColor, setNewColor] = useState('');

  const handleImageUpload = async (e, colorKey = null) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await fetch(`${API}/admin/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        
        if (colorKey) {
          setFormData(prev => ({
            ...prev,
            images: { ...prev.images, [colorKey]: data.url }
          }));
        } else {
          setFormData(prev => ({ ...prev, image_url: data.url }));
        }
        
        console.log('Success:', 'Image uploadée');
      } else {
        console.log('Error:', 'Erreur lors de l\'upload');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      try {
        const response = await fetch(`${API}/admin/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formDataUpload
        });

        if (response.ok) {
          const data = await response.json();
          uploadedUrls.push(data.url);
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    }

    setFormData(prev => ({
      ...prev,
      gallery: [...prev.gallery, ...uploadedUrls]
    }));
    setUploading(false);
    toast.success(`${uploadedUrls.length} image(s) ajoutée(s)`);
  };

  const addSpec = () => {
    if (!newSpec.key || !newSpec.value) return;
    setFormData(prev => ({
      ...prev,
      specs: { ...prev.specs, [newSpec.key]: newSpec.value }
    }));
    setNewSpec({ key: '', value: '' });
  };

  const removeSpec = (key) => {
    const newSpecs = { ...formData.specs };
    delete newSpecs[key];
    setFormData(prev => ({ ...prev, specs: newSpecs }));
  };

  const addFeature = () => {
    if (!newFeature) return;
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, newFeature]
    }));
    setNewFeature('');
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addColor = () => {
    if (!newColor || formData.colors.includes(newColor)) return;
    setFormData(prev => ({
      ...prev,
      colors: [...prev.colors, newColor]
    }));
    setNewColor('');
  };

  const removeColor = (color) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.filter(c => c !== color),
      images: Object.fromEntries(Object.entries(prev.images).filter(([k]) => k !== color))
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      console.log('Error:', 'Nom requis');
      return;
    }

    setLoading(true);
    
    // Clean up computed values before sending
    const { _marginEuros, _discountEuros, _priceHTBeforeDiscount, _finalMarginEuros, _finalMarginPercent, ...cleanData } = formData;
    
    const dataToSend = {
      ...cleanData,
      price: cleanData.price ? parseFloat(cleanData.price) : null,
      original_price: cleanData.original_price ? parseFloat(cleanData.original_price) : null,
      discount_percent: cleanData.discount_percent ? parseInt(cleanData.discount_percent) : null,
      guarantee_years: parseInt(cleanData.guarantee_years) || 4,
      category: cleanData.category || null,
      subcategory: cleanData.subcategory || null,
      badge: cleanData.badge || null,
      // Pricing fields
      purchase_price_ht: cleanData.purchase_price_ht ? parseFloat(cleanData.purchase_price_ht) : null,
      margin_value: cleanData.margin_value ? parseFloat(cleanData.margin_value) : null,
      margin_type: cleanData.margin_type || 'percent',
      discount_value: cleanData.discount_value ? parseFloat(cleanData.discount_value) : null,
      discount_type: cleanData.discount_type || 'percent',
      price_ht: cleanData.price_ht ? parseFloat(cleanData.price_ht) : null
    };

    try {
      const url = isCreating 
        ? `${API}/admin/products`
        : `${API}/admin/products/${product.product_id}`;
      
      const response = await fetch(url, {
        method: isCreating ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        console.log('Success:', isCreating ? 'Produit créé' : 'Produit mis à jour');
        onSave();
      } else {
        const data = await response.json();
        console.log('Error:', data.detail || 'Erreur');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const availableColors = [
    { value: 'black', label: 'Noir', hex: '#1a1a1a' },
    { value: 'white', label: 'Blanc', hex: '#ffffff' },
    { value: 'gray', label: 'Gris', hex: '#6b7280' },
    { value: 'brown', label: 'Marron', hex: '#92400e' },
    { value: 'red', label: 'Rouge', hex: '#dc2626' },
    { value: 'silver', label: 'Silver', hex: '#cbd5e1' },
  ];

  const selectedCategory = categories.find(c => c.value === formData.category);
  const availableSubcategories = selectedCategory?.subcategories || [];

  const sections = [
    { id: 'basic', label: 'Informations' },
    { id: 'pricing', label: 'Prix & Badges' },
    { id: 'images', label: 'Images' },
    { id: 'colors', label: 'Couleurs' },
    { id: 'specs', label: 'Spécifications' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto py-8">
      <div className="bg-fgs-surface border border-fgs-border rounded-xl w-full max-w-4xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-fgs-border">
          <h2 className="font-heading text-xl font-bold text-white">
            {isCreating ? 'Nouveau produit' : 'Modifier le produit'}
          </h2>
          <button onClick={onClose} className="text-fgs-text-muted hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Section Tabs */}
        <div className="flex gap-1 p-2 bg-fgs-bg border-b border-fgs-border">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                activeSection === section.id
                  ? 'bg-fgs-primary text-white'
                  : 'text-fgs-text-muted hover:text-white hover:bg-fgs-surface'
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          
          {/* Basic Info Section */}
          {activeSection === 'basic' && (
            <div className="space-y-4">
              <div>
                <Label className="text-fgs-text-muted text-sm">Nom du produit *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-fgs-bg border-fgs-border text-white mt-1"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-fgs-text-muted text-sm">Catégorie</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      category: e.target.value,
                      subcategory: '' // Reset subcategory when category changes
                    }))}
                    className="w-full mt-1 px-3 py-2 bg-fgs-bg border border-fgs-border rounded-lg text-white"
                  >
                    <option value="">Non classé</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-fgs-text-muted text-sm">Sous-catégorie</Label>
                  <select
                    value={formData.subcategory}
                    onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 bg-fgs-bg border border-fgs-border rounded-lg text-white"
                    disabled={!formData.category}
                  >
                    <option value="">Aucune</option>
                    {availableSubcategories.map(sub => (
                      <option key={sub} value={sub}>{subcategoryLabels[sub] || sub}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-fgs-text-muted text-sm">Description courte</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-fgs-bg border-fgs-border text-white mt-1"
                />
              </div>

              <div>
                <Label className="text-fgs-text-muted text-sm">Description longue</Label>
                <textarea
                  value={formData.description_long}
                  onChange={(e) => setFormData(prev => ({ ...prev, description_long: e.target.value }))}
                  rows={4}
                  className="w-full mt-1 px-3 py-2 bg-fgs-bg border border-fgs-border rounded-lg text-white"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-fgs-text-muted text-sm">Garantie (années)</Label>
                  <Input
                    type="number"
                    value={formData.guarantee_years}
                    onChange={(e) => setFormData(prev => ({ ...prev, guarantee_years: e.target.value }))}
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-white cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.ai_enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, ai_enabled: e.target.checked }))}
                      className="rounded"
                    />
                    Fonctionnalités IA activées
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Pricing & Badges Section */}
          {activeSection === 'pricing' && (
            <div className="space-y-6">
              {/* Professional Pricing Calculator */}
              <div className="bg-fgs-bg border border-fgs-border rounded-lg p-4">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-fgs-primary" />
                  Calculateur de prix professionnel
                </h3>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Prix d'achat HT */}
                  <div>
                    <Label className="text-fgs-text-muted text-sm">Prix d'achat HT (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.purchase_price_ht}
                      onChange={(e) => {
                        const result = recalculatePrices({ purchase_price_ht: e.target.value });
                        setFormData(result);
                      }}
                      className="bg-fgs-surface border-fgs-border text-white mt-1"
                      placeholder="0.00"
                    />
                  </div>

                  {/* Marge */}
                  <div>
                    <Label className="text-fgs-text-muted text-sm">Marge initiale</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.margin_value}
                        onChange={(e) => {
                          const result = recalculatePrices({ margin_value: e.target.value });
                          setFormData(result);
                        }}
                        className="bg-fgs-surface border-fgs-border text-white flex-1"
                        placeholder="0"
                      />
                      <select
                        value={formData.margin_type}
                        onChange={(e) => {
                          const result = recalculatePrices({ margin_type: e.target.value });
                          setFormData(result);
                        }}
                        className="w-16 px-2 py-2 bg-fgs-surface border border-fgs-border rounded-lg text-white"
                      >
                        <option value="percent">%</option>
                        <option value="euros">€</option>
                      </select>
                    </div>
                  </div>

                  {/* Remise */}
                  <div>
                    <Label className="text-fgs-text-muted text-sm">Remise client</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.discount_value}
                        onChange={(e) => {
                          const result = recalculatePrices({ discount_value: e.target.value });
                          setFormData(result);
                        }}
                        className="bg-fgs-surface border-fgs-border text-white flex-1"
                        placeholder="0"
                      />
                      <select
                        value={formData.discount_type}
                        onChange={(e) => {
                          const result = recalculatePrices({ discount_type: e.target.value });
                          setFormData(result);
                        }}
                        className="w-16 px-2 py-2 bg-fgs-surface border border-fgs-border rounded-lg text-white"
                      >
                        <option value="percent">%</option>
                        <option value="euros">€</option>
                      </select>
                    </div>
                    <p className="text-xs text-fgs-text-muted mt-1">Génère le prix barré automatiquement</p>
                  </div>

                  {/* Prix HT calculé */}
                  <div>
                    <Label className="text-fgs-text-muted text-sm">Prix vente HT (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.price_ht}
                      onChange={(e) => {
                        // Saisie libre
                        setFormData(prev => ({ ...prev, price_ht: e.target.value }));
                      }}
                      onBlur={(e) => {
                        // Recalcul inverse au blur
                        const priceHT = parseFloat(e.target.value) || 0;
                        if (priceHT > 0) {
                          const result = recalculateFromPriceHT(priceHT);
                          setFormData(result);
                        }
                      }}
                      className="bg-fgs-surface border-fgs-border text-white mt-1"
                      placeholder="Calculé"
                    />
                  </div>
                </div>

                {/* Prix TTC Final + Marge résiduelle */}
                <div className="mt-4 pt-4 border-t border-fgs-border">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Prix TTC Final */}
                    <div className="flex items-center justify-between bg-fgs-primary/5 border border-fgs-primary/30 rounded-lg p-3">
                      <div>
                        <Label className="text-fgs-primary text-sm font-medium">Prix TTC Final</Label>
                        <p className="text-xs text-fgs-text-muted">TVA 20% incluse • Entrée libre</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => {
                            // Saisie libre - on ne recalcule PAS à chaque frappe
                            setFormData(prev => ({ ...prev, price: e.target.value }));
                          }}
                          onBlur={(e) => {
                            // Recalcul inverse UNIQUEMENT quand on quitte le champ
                            const priceTTC = parseFloat(e.target.value) || 0;
                            if (priceTTC > 0) {
                              const result = recalculateFromPriceTTC(priceTTC);
                              setFormData(result);
                            }
                          }}
                          className="bg-fgs-primary/10 border-fgs-primary text-fgs-primary text-xl font-bold w-28 text-right"
                          placeholder="0.00"
                        />
                        <span className="text-fgs-primary text-xl font-bold">€</span>
                      </div>
                    </div>

                    {/* Marge résiduelle après remise */}
                    <div className="bg-fgs-surface border border-fgs-border rounded-lg p-3">
                      <Label className="text-fgs-text-muted text-sm font-medium">Marge résiduelle après remise</Label>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex-1">
                          <span className="text-2xl font-bold text-white">
                            {formData._finalMarginEuros !== undefined ? formData._finalMarginEuros.toFixed(2) : '—'} €
                          </span>
                        </div>
                        <div className="text-right">
                          <span className={`text-lg font-semibold ${formData._finalMarginPercent >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {formData._finalMarginPercent !== undefined ? formData._finalMarginPercent.toFixed(1) : '—'}%
                          </span>
                          <p className="text-xs text-fgs-text-muted">du prix d'achat</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Résumé du calcul */}
                <div className="mt-4 p-3 bg-fgs-surface rounded-lg text-sm">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                    <div>
                      <p className="text-fgs-text-muted text-xs">Achat HT</p>
                      <p className="text-white font-mono">{parseFloat(formData.purchase_price_ht || 0).toFixed(2)} €</p>
                    </div>
                    <div>
                      <p className="text-fgs-text-muted text-xs">+ Marge</p>
                      <p className="text-green-500 font-mono">+{formData._marginEuros !== undefined ? formData._marginEuros.toFixed(2) : '0.00'} €</p>
                    </div>
                    <div>
                      <p className="text-fgs-text-muted text-xs">- Remise</p>
                      <p className="text-red-500 font-mono">-{formData._discountEuros !== undefined ? formData._discountEuros.toFixed(2) : '0.00'} €</p>
                    </div>
                    <div>
                      <p className="text-fgs-text-muted text-xs">= Prix HT</p>
                      <p className="text-fgs-primary font-mono font-bold">{parseFloat(formData.price_ht || 0).toFixed(2)} €</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Display price (manual override) */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-fgs-text-muted text-sm">Prix barré TTC (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.original_price}
                    onChange={(e) => setFormData(prev => ({ ...prev, original_price: e.target.value }))}
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                    placeholder="Auto-calculé"
                  />
                  <p className="text-xs text-fgs-text-muted mt-1">
                    Auto: {formData._priceHTBeforeDiscount ? (formData._priceHTBeforeDiscount * 1.20).toFixed(2) : '—'} € TTC
                  </p>
                </div>
                <div>
                  <Label className="text-fgs-text-muted text-sm">Badge remise (%)</Label>
                  <Input
                    type="number"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_percent: e.target.value }))}
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                    placeholder="Auto-calculé"
                  />
                  <p className="text-xs text-fgs-text-muted mt-1">
                    Badge affiché : -{formData.discount_percent || 'X'}%
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="inStock"
                  checked={formData.in_stock !== false}
                  onChange={(e) => setFormData(prev => ({ ...prev, in_stock: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="inStock" className="text-white cursor-pointer">Produit en stock</Label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="comingSoon"
                  data-testid="checkbox-coming-soon"
                  checked={formData.coming_soon === true}
                  onChange={(e) => setFormData(prev => ({ ...prev, coming_soon: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="comingSoon" className="text-white cursor-pointer">
                  🔜 Bientôt disponible <span className="text-fgs-text-muted text-xs">(non commandable, badge orange affiché)</span>
                </Label>
              </div>

              <div>
                <Label className="text-fgs-text-muted text-sm mb-3 block">Badge produit</Label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, badge: '' }))}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      !formData.badge
                        ? 'bg-fgs-primary text-white'
                        : 'bg-fgs-bg text-fgs-text-muted hover:text-white'
                    }`}
                  >
                    Aucun
                  </button>
                  {badgeOptions.map(badge => (
                    <button
                      key={badge.value}
                      onClick={() => setFormData(prev => ({ ...prev, badge: badge.value }))}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        formData.badge === badge.value
                          ? `${badge.color} text-white`
                          : 'bg-fgs-bg text-fgs-text-muted hover:text-white'
                      }`}
                    >
                      {badge.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="bg-fgs-bg rounded-lg p-4">
                <Label className="text-fgs-text-muted text-sm mb-2 block">Aperçu</Label>
                <div className="flex items-center gap-3">
                  {formData.badge && (
                    <span className={`px-2 py-0.5 rounded text-xs text-white ${badgeOptions.find(b => b.value === formData.badge)?.color}`}>
                      {badgeOptions.find(b => b.value === formData.badge)?.label}
                    </span>
                  )}
                  {formData.discount_percent && (
                    <span className="px-2 py-0.5 rounded text-xs bg-red-500 text-white">
                      -{formData.discount_percent}%
                    </span>
                  )}
                  <div>
                    {formData.price && (
                      <span className="text-fgs-primary font-mono font-bold text-lg">{parseFloat(formData.price).toFixed(2)} €</span>
                    )}
                    {formData.original_price && (
                      <span className="ml-2 text-sm text-fgs-text-muted line-through">
                        {parseFloat(formData.original_price).toFixed(2)} €
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Images Section */}
          {activeSection === 'images' && (
            <div className="space-y-6">
              {/* Main Image */}
              <div>
                <Label className="text-fgs-text-muted text-sm mb-2 block">Image principale</Label>
                <div className="flex items-start gap-4">
                  {formData.image_url ? (
                    <img src={formData.image_url} alt="Product" className="w-32 h-32 object-contain bg-white rounded-lg" />
                  ) : (
                    <div className="w-32 h-32 bg-fgs-bg border border-dashed border-fgs-border rounded-lg flex items-center justify-center">
                      <Image className="w-8 h-8 text-fgs-text-muted" />
                    </div>
                  )}
                  <div className="flex-1">
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="URL de l'image ou uploader"
                      className="bg-fgs-bg border-fgs-border text-white mb-2"
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={(e) => handleImageUpload(e)}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="border-fgs-border text-white"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                      Uploader
                    </Button>
                  </div>
                </div>
              </div>

              {/* Gallery */}
              <div>
                <Label className="text-fgs-text-muted text-sm mb-2 block">
                  Galerie d'images ({formData.gallery.length} images)
                </Label>
                <div className="grid grid-cols-6 gap-2 mb-3">
                  {formData.gallery.map((url, i) => (
                    <div key={i} className="relative group aspect-square">
                      <img src={url} alt={`Gallery ${i}`} className="w-full h-full object-contain bg-white rounded" />
                      <button
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          gallery: prev.gallery.filter((_, idx) => idx !== i) 
                        }))}
                        className="absolute top-1 right-1 p-1 bg-fgs-accent rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
                <input
                  type="file"
                  id="gallery-upload"
                  onChange={handleGalleryUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('gallery-upload')?.click()}
                  disabled={uploading}
                  className="border-fgs-border text-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Ajouter des images à la galerie
                </Button>
              </div>
            </div>
          )}

          {/* Colors Section */}
          {activeSection === 'colors' && (
            <div className="space-y-4">
              <p className="text-sm text-fgs-text-muted">
                Ajoutez les couleurs disponibles et uploadez une image spécifique pour chaque couleur.
              </p>
              
              {formData.colors.map(color => (
                <div key={color} className="flex items-center gap-3 p-3 bg-fgs-bg rounded-lg">
                  <span
                    className="w-8 h-8 rounded-full border-2 border-fgs-border flex-shrink-0"
                    style={{ backgroundColor: availableColors.find(c => c.value === color)?.hex || color }}
                  />
                  <span className="text-white capitalize flex-1">{availableColors.find(c => c.value === color)?.label || color}</span>
                  
                  {/* Image for this color */}
                  <div className="flex items-center gap-2">
                    {formData.images[color] ? (
                      <img src={formData.images[color]} alt={color} className="w-12 h-12 object-contain bg-white rounded" />
                    ) : (
                      <span className="text-xs text-fgs-text-muted bg-fgs-surface px-2 py-1 rounded">Pas d'image</span>
                    )}
                    <input
                      type="file"
                      id={`color-${color}`}
                      onChange={(e) => handleImageUpload(e, color)}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById(`color-${color}`)?.click()}
                      className="border-fgs-border text-white"
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <button
                    onClick={() => removeColor(color)}
                    className="p-2 text-fgs-accent hover:bg-fgs-accent/20 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {/* Add Color */}
              <div className="flex gap-2">
                <select
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="flex-1 px-3 py-2 bg-fgs-bg border border-fgs-border rounded-lg text-white"
                >
                  <option value="">Ajouter une couleur...</option>
                  {availableColors.filter(c => !formData.colors.includes(c.value)).map(color => (
                    <option key={color.value} value={color.value}>{color.label}</option>
                  ))}
                </select>
                <Button
                  type="button"
                  onClick={addColor}
                  disabled={!newColor}
                  className="bg-fgs-primary hover:bg-fgs-primary-hover"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Specs & Features Section */}
          {activeSection === 'specs' && (
            <div className="space-y-6">
              {/* Specs */}
              <div>
                <Label className="text-fgs-text-muted text-sm mb-2 block">Spécifications techniques</Label>
                <div className="space-y-2 mb-3">
                  {Object.entries(formData.specs).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2 p-2 bg-fgs-bg rounded">
                      <span className="text-fgs-text-muted text-sm w-32">{key}</span>
                      <span className="text-white text-sm flex-1">{value}</span>
                      <button onClick={() => removeSpec(key)} className="text-fgs-accent p-1 hover:bg-fgs-accent/20 rounded">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newSpec.key}
                    onChange={(e) => setNewSpec(prev => ({ ...prev, key: e.target.value }))}
                    placeholder="Clé (ex: Portée)"
                    className="bg-fgs-bg border-fgs-border text-white"
                  />
                  <Input
                    value={newSpec.value}
                    onChange={(e) => setNewSpec(prev => ({ ...prev, value: e.target.value }))}
                    placeholder="Valeur (ex: 1800m)"
                    className="bg-fgs-bg border-fgs-border text-white"
                  />
                  <Button type="button" onClick={addSpec} className="bg-fgs-primary">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Features */}
              <div>
                <Label className="text-fgs-text-muted text-sm mb-2 block">Caractéristiques</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.features.map((feature, i) => (
                    <span key={i} className="flex items-center gap-1 px-2 py-1 bg-fgs-bg rounded text-white text-sm">
                      {feature}
                      <button onClick={() => removeFeature(i)} className="text-fgs-accent ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="Ajouter une caractéristique"
                    className="bg-fgs-bg border-fgs-border text-white"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <Button type="button" onClick={addFeature} className="bg-fgs-primary">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-fgs-border">
          <Button variant="outline" onClick={onClose} className="border-fgs-border text-white">
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-fgs-primary hover:bg-fgs-primary-hover"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            {isCreating ? 'Créer' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
